<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ROUTES } from '@/router/routeNames';
import { useToast } from 'primevue/usetoast';
import api from '@/services/axios';
import PrimaryButton from '@/components/general/PrimaryButton.vue';
import SecondaryButton from '@/components/general/SecondaryButton.vue';
import DangerButton from '@/components/general/DangerButton.vue';
import ConfirmationModal from '@/components/general/ConfirmationModal.vue';
// import Dropdown from '@/components/general/Dropdown.vue';
// import DropdownLink from '@/components/general/DropdownLink.vue';
import { nairaSymbol } from '@/constants';

const router = useRouter();
const toast = useToast();

const products = ref([]);
const isLoading = ref(true);
const selectedProduct = ref(null);
const showDeleteDialog = ref(false);

const fetchProducts = async () => {
  try {
    isLoading.value = true;
    // Make sure we're using the correct API endpoint path
    const response = await api.get('api/business/products');
    products.value = response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load products',
      life: 3000
    });
  } finally {
    isLoading.value = false;
  }
};

// const confirmDelete = (product) => {
//   selectedProduct.value = product;
//   showDeleteDialog.value = true;
// };

const deleteProduct = async () => {
  if (!selectedProduct.value) return;

  try {
    await api.delete(`api/business/products/${selectedProduct.value.uuid}`);
    mixpanel.
    toast.add({
      severity: 'success',
      summary: 'Product Deleted',
      detail: 'Product has been deleted successfully',
      life: 3000
    });
    mixpanel.track('Delete Product was clicked from Products Index Page', { product_name: selectedProduct.value?.name, product_uuid: selectedProduct.value?.uuid, business_name: selectedProduct.value?.business.name, price: selectedProduct.value?.price });
    await fetchProducts(); // Refresh the list
    showDeleteDialog.value = false;
  } catch (error) {
    console.error('Error deleting product:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to delete product',
      life: 3000
    });
  }
};

const viewProduct = (productUuid) => {
  router.push({ name: ROUTES.PRODUCT, params: { id: productUuid } });
};

// const editProduct = (productUuid) => {
//   router.push({ name: ROUTES.PRODUCT_EDIT, params: { id: productUuid } });
// };

const createProduct = () => {
  mixpanel.track('Add Product was clicked from Products Index Page');
  router.push({ name: ROUTES.PRODUCT_CREATE });
};

onMounted(() => {
  fetchProducts();
});
</script>

<template>
    <div class="sm:py-12">
      <div class="sm:px-6 lg:px-8">
        <div class="overflow-hidden bg-white shadow-sm dark:bg-gray-800 sm:rounded-lg">
          <!-- Loading State -->
          <div v-if="isLoading" class="flex justify-center items-center p-12">
            <div class="w-12 h-12 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"></div>
          </div>

          <!-- Empty State -->
          <div v-else-if="products.length === 0" class="p-12 text-center">
            <div class="mb-6">
              <svg class="mx-auto w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No products</h3>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new product.</p>
              <div class="mt-6">
                <PrimaryButton @click="createProduct">
                  <i class="pi pi-plus"></i>
                  Add Product
                </PrimaryButton>
              </div>
            </div>
          </div>

          <!-- Products Table -->
          <div v-else class="p-6 text-gray-900 dark:text-gray-100">
            <div class="flex justify-end mb-4">
              <PrimaryButton @click="createProduct">
                <i class="pi pi-plus"></i>
                Add Product
              </PrimaryButton>
            </div>
            <div class="overflow-x-auto relative">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">
                      Image
                    </th>
                    <th class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">
                      Name
                    </th>
                    <th class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">
                      Description
                    </th>
                    <th class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">
                      Price
                    </th>
                    <th class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">
                      Stock
                    </th>
                    <!-- <th class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">
                      Actions
                    </th> -->
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                  <tr v-for="product in products" :key="product.uuid" @click="viewProduct(product.uuid)" class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <img
                        v-if="product.image_url"
                        :src="product.image_url"
                        :alt="product.name"
                        class="object-cover w-10 h-10 rounded-full"
                      />
                      <div
                        v-else
                        class="flex justify-center items-center w-10 h-10 bg-gray-200 rounded-full dark:bg-gray-600"
                      >
                        <svg
                          class="w-6 h-6 text-gray-400 dark:text-gray-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm font-medium text-gray-900 dark:text-white" >
                        {{ product.name }}
                      </div>
                    </td>
                    <td class="px-6 py-4">
                      <div class="max-w-xs text-sm text-gray-500 truncate dark:text-gray-300">
                        {{ product.description }}
                      </div>
                    </td>
                    <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap dark:text-gray-300">
                      {{ nairaSymbol }}{{ (parseFloat(product.price) || 0).toFixed() }}
                    </td>
                    <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap dark:text-gray-300">
                      {{ product.stock ? product.stock : product.is_available ? 'Available' : 'Not Available' }}
                    </td>
                    <!-- <td class="px-6 py-4 text-sm font-medium whitespace-nowrap">
                      <div class="flex relative items-center">
                        <Dropdown align="right" width="48">
                          <template #trigger>
                            <button class="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none">
                              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                              </svg>
                            </button>
                          </template>
                          <template #content>
                            <DropdownLink as="button" @click="viewProduct(product.uuid)">
                              <div class="flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" class="mr-2 w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                  <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
                                </svg>
                                View
                              </div>
                            </DropdownLink>
                            <DropdownLink as="button" @click="editProduct(product.uuid)">
                              <div class="flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" class="mr-2 w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                </svg>
                                Edit
                              </div>
                            </DropdownLink>
                            <DropdownLink as="button" @click="confirmDelete(product)">
                              <div class="flex items-center text-red-600 dark:text-red-400">
                                <svg xmlns="http://www.w3.org/2000/svg" class="mr-2 w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                                </svg>
                                Delete
                              </div>
                            </DropdownLink>
                          </template>
                        </Dropdown>
                      </div>
                    </td> -->
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    <!-- Delete Confirmation Dialog -->
      <ConfirmationModal :show="showDeleteDialog" @close="showDeleteDialog = false">
        <template #title>
          Delete Product
        </template>
        <template #content>
          <p>
            Are you sure you want to delete "{{ selectedProduct?.name }}"? This action cannot be undone.
          </p>
        </template>
        <template #footer>
          <SecondaryButton class="mr-2" @click="showDeleteDialog = false">
            Cancel
          </SecondaryButton>
          <DangerButton @click="deleteProduct">
            Delete
          </DangerButton>
        </template>
      </ConfirmationModal>
    </div>

</template>
